// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SafeDictionary } from "@insite/client-framework/Common/Types";
import isApiError from "@insite/client-framework/Common/isApiError";
import { ApiParameter, get, HasPagingParameters, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { BaseModel, PaginationModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

export interface PreviouslyPurchasedFilters {
    fromDate?: string;
    toDate?: string;
    pacePartNumber?: string;
    manufacturerNumber?: string;
    customerProductNumber?: string;
    fromQty?: number;
    toQty?: number;
}

export interface PreviouslyPurchasedApiParameter extends ApiParameter, HasPagingParameters {
    filters: PreviouslyPurchasedFilters;
    sortBy: {
        orderDateSortType: "asc" | "desc";
    };
}

export interface PreviouslyPurchasedModel extends BaseModel {
    id: string;
    productErpNumber: string;
    unitOfMeasure: string;
    lineNumber: number;
    qtyOrdered: number;
    qtyShipped: number;
    orderDate: string;
    customerProductNumber: string | null;
    manufacturerItem: string | null;
}

export interface PreviouslyPurchasedCollectionModel extends BaseModel {
    previouslyPurchasedProductList: PreviouslyPurchasedModel[] | null;
    pagination: PaginationModel | null;
    productCollectionApiModel: {
        products: ProductModel[];
    };
}

const previouslyPurchasedUrl = "/api/pace/PreviouslyPurchasedProducts/GetPreviouslyPurchasedProducts";

export async function getPreviouslyPurchased(
    parameter: SafeDictionary<string | boolean>,
): Promise<ServiceResult<PreviouslyPurchasedCollectionModel>> {
    try {
        const previouslyPurchasedProducts = await get<PreviouslyPurchasedCollectionModel>(
            previouslyPurchasedUrl,
            parameter,
        );
        const previouslyPurchasedPopulated = {
            ...previouslyPurchasedProducts,
            previouslyPurchasedProductList:
                previouslyPurchasedProducts?.previouslyPurchasedProductList?.map(p => ({
                    ...p,
                    id: p.productErpNumber,
                })) || [],
        };
        return {
            successful: true,
            result: previouslyPurchasedPopulated,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}
